import React from 'react';
import { useCall } from '@/contexts/CallContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, PhoneOff, User } from 'lucide-react';

/**
 * Incoming call dialog that shows Accept/Reject UI
 * Displays when there's a pending incoming call before agent accepts
 * Modal overlay that requires user action
 */
export function IncomingCallDialog() {
  const callContext = useCall();

  // Don't render if context not available or no pending call
  if (!callContext || !callContext.pendingIncomingCall) {
    return null;
  }

  const { pendingIncomingCall, acceptIncomingCall, rejectIncomingCall } = callContext;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 animate-in fade-in duration-200">
      <Card className="w-96 shadow-2xl border-2 border-green-500/50 animate-in zoom-in-95 duration-300">
        <CardContent className="p-6 text-center space-y-4">
          {/* Pulsing phone icon */}
          <div className="mx-auto w-20 h-20 rounded-full bg-green-500 flex items-center justify-center animate-pulse shadow-lg shadow-green-500/50">
            <Phone className="h-10 w-10 text-white" />
          </div>

          {/* Caller info */}
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-foreground">Incoming Call</h2>
            <p className="text-lg font-semibold text-foreground">
              {pendingIncomingCall.callerName || 'Unknown Caller'}
            </p>
            <p className="text-muted-foreground font-mono text-sm">
              {pendingIncomingCall.callerPhone}
            </p>
          </div>

          {/* Customer badge if found */}
          {pendingIncomingCall.customerId && (
            <Badge variant="secondary" className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              Existing Customer
            </Badge>
          )}

          {/* Accept/Reject buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              variant="destructive"
              className="flex-1 h-12 text-base"
              onClick={rejectIncomingCall}
            >
              <PhoneOff className="mr-2 h-5 w-5" />
              Reject
            </Button>
            <Button
              className="flex-1 h-12 text-base bg-green-600 hover:bg-green-700 text-white"
              onClick={acceptIncomingCall}
            >
              <Phone className="mr-2 h-5 w-5" />
              Accept
            </Button>
          </div>

          {/* Help text */}
          <p className="text-xs text-muted-foreground">
            Call will go to voicemail if not answered within 20 seconds
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
